/**
 * useClientFeatureFlags Hook
 *
 * Custom hook to access client-side feature flags from ClientFeatureFlagsProvider.
 * Provides access to feature flag values and methods to toggle/set them.
 *
 * Currently supports:
 * - reactProfilerEnabled: Controls whether React Profiler is enabled (default: true)
 * @module hooks/useClientFeatureFlags
 * @returns The current client feature flags context value
 * @example
 * ```tsx
 * import { useClientFeatureFlags } from "@/hooks/useClientFeatureFlags";
 *
 * const MyComponent = () => {
 *   const { reactProfilerEnabled, toggleReactProfiler, setReactProfilerEnabled } = useClientFeatureFlags();
 *
 *   return (
 *     <button onClick={toggleReactProfiler}>
 *       Profiler is {reactProfilerEnabled ? "enabled" : "disabled"}
 *     </button>
 *   );
 * };
 * ```
 */

import { useContext } from "react";

import { ClientFeatureFlagsContext } from "@/providers/ClientFeatureFlagsProvider";

/**
 * Hook to access client feature flags
 * @returns The current client feature flags context value
 */
export const useClientFeatureFlags = () =>
  useContext(ClientFeatureFlagsContext);
